import React, { useState, useEffect, useRef, useCallback } from 'react';
import { imageProps } from '../utils/images';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import Layout from '../components/Layout';
import api from '../api';
import roadmapsApi from '../roadmapsApi';
import { useAuth } from '../contexts/AuthContext';
import { LEARNING_PATHS } from '../seo/learningPaths';

// ── Path config ───────────────────────────────────────────────────────────────

export { LEARNING_PATHS };

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractDay(title) {
  const m = title?.match(/^Day\s+(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(i18n.language || 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Day card ──────────────────────────────────────────────────────────────────

function DayCard({ post, dayNum, path, index }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLocked = post.locked && !path.allAccessible;

  const handleClick = () => {
    if (isLocked) return;
    navigate(`/${post.slug}`);
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLocked}
      className="w-full text-left rounded-2xl border transition-all group"
      style={{
        backgroundColor: 'var(--lx-card)',
        borderColor: 'var(--lx-border)',
        cursor: isLocked ? 'default' : 'pointer',
        opacity: isLocked ? 0.6 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isLocked) {
          e.currentTarget.style.borderColor = path.accentBorder;
          e.currentTarget.style.backgroundColor = path.accentBg;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--lx-border)';
        e.currentTarget.style.backgroundColor = 'var(--lx-card)';
      }}
    >
      <div className="p-4">
        {/* Day badge + lock */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{ background: path.accentBg, border: `1px solid ${path.accentBorder}`, color: path.accentText }}
          >
            {t('learningPath.day', { n: dayNum ?? index + 1 })}
          </span>
          {isLocked ? (
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          ) : path.allAccessible && post.locked ? (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#f59e0b" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 19l2-8 5 4 3-7 3 7 5-4 2 8H2zm18-11a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
            </svg>
          ) : post.reading_time ? (
            <span className="text-xs text-gray-600">{t('learningPath.minShort', { n: post.reading_time })}</span>
          ) : null}
        </div>

        {/* Feature image thumbnail */}
        {post.feature_image && (
          <div className="mb-3 rounded-lg overflow-hidden h-24 w-full">
            <img
              {...imageProps(post.feature_image, 400, '400px')}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy" decoding="async"
            />
          </div>
        )}

        {/* Title */}
        <p className="text-sm font-semibold text-white leading-snug line-clamp-2">
          {post.title}
        </p>

        {/* Date */}
        <p className="text-xs text-gray-600 mt-2">{formatDate(post.published_at)}</p>
      </div>
    </button>
  );
}

// ── Coming soon placeholder ───────────────────────────────────────────────────

function ComingSoonCard({ dayNum, path }) {
  const { t } = useTranslation();
  return (
    <div
      className="w-full rounded-2xl border p-4 opacity-40"
      style={{ backgroundColor: 'var(--lx-card)', borderColor: 'var(--lx-border)', borderStyle: 'dashed' }}
    >
      <span
        className="text-xs font-bold px-2.5 py-1 rounded-full"
        style={{ background: path.accentBg, border: `1px solid ${path.accentBorder}`, color: path.accentText }}
      >
        {t('learningPath.day', { n: dayNum })}
      </span>
      <p className="text-xs text-gray-600 mt-3">{t('learningPath.comingSoonCard')}</p>
    </div>
  );
}

// ── Path selector tabs ────────────────────────────────────────────────────────

function PathTabs({ current }) {
  return (
    <div className="flex gap-1 p-1 rounded-xl mb-8 w-fit" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
      {Object.values(LEARNING_PATHS).map((p) => (
        <Link
          key={p.slug}
          to={`/learning-paths/${p.slug}`}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            current === p.slug ? 'text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'
          }`}
          style={current === p.slug ? { background: `linear-gradient(135deg, ${p.accentFrom}, ${p.accentTo})` } : {}}
        >
          <span>{p.icon}</span>
          <span className="hidden sm:inline">{p.shortLabel}</span>
        </Link>
      ))}
    </div>
  );
}

// ── In-memory cache — survives tab switches, cleared on page reload ───────────
const pathCache = {};

// ── AI Roadmap Generator ──────────────────────────────────────────────────────

const ROADMAP_TOPICS = [
  { labelKey: 'learningPath.chipDevopsEng', days: 60, prompt: 'Become a DevOps engineer from scratch — Linux, Docker, Kubernetes, CI/CD, monitoring' },
  { labelKey: 'learningPath.chipK8sExpert', days: 30, prompt: 'Master Kubernetes from basics to production-grade deployments' },
  { labelKey: 'learningPath.chipAws',       days: 30, prompt: 'Learn AWS cloud services, architecture, and best practices' },
  { labelKey: 'learningPath.chipAiml',      days: 45, prompt: 'Transition into AI/ML engineering with practical projects' },
  { labelKey: 'learningPath.chipTerraform', days: 14, prompt: 'Master Terraform for infrastructure as code' },
  { labelKey: 'learningPath.chipPython',    days: 21, prompt: 'Learn Python specifically for DevOps automation and scripting' },
  { labelKey: 'learningPath.chipDocker',    days: 14, prompt: 'Learn Docker, containerization, and container orchestration' },
  { labelKey: 'learningPath.chipSre',       days: 45, prompt: 'Become a Site Reliability Engineer from a software background' },
  { labelKey: 'learningPath.chipGha',       days: 14, prompt: 'Master GitHub Actions for CI/CD pipelines and automation' },
  { labelKey: 'learningPath.chipPlatform',  days: 30, prompt: 'Build internal developer platforms with Kubernetes and GitOps' },
];

const ROADMAP_PROGRESS_KEYS = [
  'learningPath.rmProg1',
  'learningPath.rmProg2',
  'learningPath.rmProg3',
  'learningPath.rmProg4',
  'learningPath.rmProg5',
  'learningPath.rmProg6',
];

const EXPORT_FORMATS = [
  { id: 'pdf',  label: 'PDF',  icon: '📄' },
  { id: 'docx', label: 'DOCX', icon: '📝' },
  { id: 'html', label: 'HTML', icon: '🌐' },
  { id: 'txt',  label: 'TXT',  icon: '📃' },
];

function RoadmapQuotaMeter({ usage, isLifetime }) {
  const { t } = useTranslation();
  if (!usage) return null;
  const pct   = usage.limit > 0 ? (usage.used / usage.limit) * 100 : 0;
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#6366f1';
  const remainingText = usage.remaining > 0
    ? (isLifetime
        ? t('learningPath.rmRemainingToday', { count: usage.remaining })
        : t('learningPath.rmRemainingMonth', { count: usage.remaining }))
    : (isLifetime ? t('learningPath.rmExhaustedToday') : t('learningPath.rmExhaustedMonth'));
  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {isLifetime ? t('learningPath.rmQuotaDailyLabel') : t('learningPath.rmQuotaMonthlyLabel')}
        </span>
        <span className="text-xs font-bold" style={{ color }}>
          {t('learningPath.rmQuotaUsedCount', { used: usage.used, limit: usage.limit })}
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--lx-muted)' }}>
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <p className="text-xs text-gray-600 mt-1.5">{remainingText}</p>
    </div>
  );
}

function RoadmapExportBtn({ roadmapId }) {
  const { t } = useTranslation();
  const [open, setOpen]         = useState(false);
  const [exporting, setExp]     = useState('');
  const [expError, setExpError] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doExport = async (fmt) => {
    setOpen(false); setExp(fmt); setExpError('');
    try {
      const { data } = await roadmapsApi.get(`/roadmaps/ai/${roadmapId}/export?format=${fmt}`);
      window.open(data.download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setExpError(err.response?.data?.error || t('learningPath.rmExportFailed'));
    } finally { setExp(''); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)} disabled={!!exporting}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
        style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)', color: '#9ca3af' }}
        onMouseEnter={e => e.currentTarget.style.color = '#f9fafb'}
        onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
      >
        {exporting ? <><div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />{t('learningPath.rmExporting')}</> : <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {t('learningPath.rmExport')}
          <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-36 rounded-xl py-1.5 z-50 shadow-xl shadow-black/30"
          style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
          {EXPORT_FORMATS.map(f => (
            <button key={f.id} onClick={() => doExport(f.id)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <span>{f.icon}</span>{f.label}
            </button>
          ))}
        </div>
      )}
      {expError && <p className="absolute right-0 top-full mt-1 text-xs text-red-400 whitespace-nowrap">{expError}</p>}
    </div>
  );
}

function RoadmapDetail({ item, onClose }) {
  const { t } = useTranslation();
  const r = item.roadmap || {};
  const [expandedDays, setExpandedDays] = useState(new Set([1]));
  const toggleDay = (day) => setExpandedDays(prev => {
    const next = new Set(prev);
    next.has(day) ? next.delete(day) : next.add(day);
    return next;
  });

  return (
    <div className="rounded-2xl p-6 space-y-6" style={{ background: 'var(--lx-surface)', border: '1px solid rgba(99,102,241,0.3)' }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {r.difficulty && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }}>
                {r.difficulty}
              </span>
            )}
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', color: '#818cf8' }}>
              📅 {t('learningPath.rmDaysBadge', { count: item.total_days })}
            </span>
          </div>
          <h2 className="text-xl font-bold text-white">{r.title || item.topic}</h2>
          {r.overview && <p className="text-gray-400 text-sm mt-1 leading-relaxed">{r.overview}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <RoadmapExportBtn roadmapId={item.roadmap_id} />
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xs transition-colors">✕ {t('learningPath.rmClose')}</button>
        </div>
      </div>

      {/* Daily plan */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('learningPath.rmDayByDay')}</h3>
        <div className="space-y-2">
          {(r.daily_plan || []).map(d => (
            <div key={d.day} className="rounded-xl border overflow-hidden transition-all"
              style={{ borderColor: expandedDays.has(d.day) ? 'rgba(99,102,241,0.4)' : 'var(--lx-border)', background: expandedDays.has(d.day) ? 'rgba(99,102,241,0.04)' : 'var(--lx-card)' }}>
              <button onClick={() => toggleDay(d.day)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                <span className="flex-shrink-0 w-12 h-6 rounded bg-indigo-600/30 text-indigo-300 text-xs font-bold flex items-center justify-center">
                  {t('learningPath.day', { n: d.day })}
                </span>
                <span className="flex-1 text-sm font-medium text-white">{d.title}</span>
                <span className="text-gray-600 text-xs transition-transform duration-200"
                  style={{ transform: expandedDays.has(d.day) ? 'rotate(180deg)' : 'none' }}>▾</span>
              </button>
              {expandedDays.has(d.day) && (
                <div className="px-4 pb-4 space-y-2 ml-15">
                  {d.focus && <p className="text-xs text-indigo-300 italic">{d.focus}</p>}
                  {d.tasks?.length > 0 && (
                    <ul className="space-y-1">
                      {d.tasks.map((t, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                          <span className="text-indigo-500 flex-shrink-0 mt-0.5">•</span>{t}
                        </li>
                      ))}
                    </ul>
                  )}
                  {d.resource && <p className="text-xs text-gray-500"><span className="text-gray-400 font-medium">{t('learningPath.rmResource')}</span> {d.resource}</p>}
                  {d.exercise && <p className="text-xs text-indigo-400"><span className="font-medium">{t('learningPath.rmExercise')}</span> {d.exercise}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Milestones + outcomes + tools */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {r.milestones?.length > 0 && (
          <div className="rounded-xl p-4" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
            <h4 className="text-xs font-semibold text-sky-400 uppercase tracking-wider mb-2">🏁 {t('learningPath.rmMilestones')}</h4>
            {r.milestones.map((m, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-sky-300">{m.period}</p>
                <p className="text-xs text-gray-500">{m.achievement}</p>
              </div>
            ))}
          </div>
        )}
        {r.learning_outcomes?.length > 0 && (
          <div className="rounded-xl p-4" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
            <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">📚 {t('learningPath.rmOutcomes')}</h4>
            <ul className="space-y-1">
              {r.learning_outcomes.map((o, i) => (
                <li key={i} className="text-xs text-gray-400 flex items-start gap-1.5">
                  <span className="text-emerald-500 flex-shrink-0">✓</span>{o}
                </li>
              ))}
            </ul>
          </div>
        )}
        {r.recommended_tools?.length > 0 && (
          <div className="rounded-xl p-4" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
            <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">🔧 {t('learningPath.rmTools')}</h4>
            <div className="flex flex-wrap gap-1.5">
              {r.recommended_tools.map((t, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded"
                  style={{ background: 'var(--lx-muted)', color: '#9ca3af' }}>{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AiRoadmapTab({ isLifetime, isPaidUser }) {
  const { t } = useTranslation();
  const [topic,        setTopic]       = useState('');
  const [days,         setDays]        = useState(30);
  const [generating,   setGenerating]  = useState(false);
  const [progressStep, setProgressStep]= useState(0);
  const [genError,     setGenError]    = useState('');
  const [currentRoadmap, setCurrentRoadmap] = useState(null);
  const [usage,        setUsage]       = useState(null);
  const [roadmaps,     setRoadmaps]    = useState(null);
  const [histLoading,  setHistLoading] = useState(true);
  const [viewRoadmap,  setViewRoadmap] = useState(null);

  const pollingRef  = useRef(null);
  const progressRef = useRef(null);
  const resultsRef  = useRef(null);

  useEffect(() => () => { clearInterval(pollingRef.current); clearInterval(progressRef.current); }, []);

  useEffect(() => {
    if (!isPaidUser) { setHistLoading(false); return; }
    roadmapsApi.get('/roadmaps/ai/list')
      .then(({ data }) => { setRoadmaps(data.roadmaps || []); setUsage(data.usage); })
      .catch(() => setRoadmaps([]))
      .finally(() => setHistLoading(false));
  }, [isPaidUser]);

  const stopPolling = () => { clearInterval(pollingRef.current); clearInterval(progressRef.current); };

  const startPolling = useCallback((jobId, topicUsed) => {
    progressRef.current = setInterval(() => setProgressStep(s => (s + 1) % ROADMAP_PROGRESS_KEYS.length), 4000);
    const poll = async () => {
      try {
        const { data } = await roadmapsApi.get(`/roadmaps/ai/jobs/${jobId}`);
        if (data.status === 'completed') {
          stopPolling();
          try {
            const { data: rd } = await roadmapsApi.get(`/roadmaps/ai/${data.roadmap_id}`);
            setCurrentRoadmap(rd.roadmap);
            setRoadmaps(prev => [{
              roadmap_id: data.roadmap_id, topic: topicUsed,
              topic_preview: topicUsed.substring(0, 120),
              total_days: days,
              created_at: new Date().toISOString(),
              roadmap: rd.roadmap.roadmap,
            }, ...(prev || [])]);
          } catch { setGenError(t('learningPath.rmErrLoadFailed')); }
          setGenerating(false);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        } else if (data.status === 'failed') {
          stopPolling(); setGenerating(false);
          setGenError(data.error || t('learningPath.rmGenFailed'));
        }
      } catch { /* keep polling */ }
    };
    poll();
    pollingRef.current = setInterval(poll, 3000);
  }, [days, t]);

  const generate = async () => {
    if (!topic.trim() || topic.trim().length < 5) { setGenError(t('learningPath.rmErrDescribe')); return; }
    setGenerating(true); setGenError(''); setCurrentRoadmap(null); setViewRoadmap(null); setProgressStep(0);
    try {
      const { data } = await roadmapsApi.post('/roadmaps/ai/generate/start', { topic: topic.trim(), days });
      if (data.usage) setUsage(data.usage);
      startPolling(data.job_id, topic.trim());
    } catch (err) {
      setGenerating(false);
      const isLimit = err.response?.status === 429;
      const msg = err.response?.data?.error || t('learningPath.rmErrStart');
      setGenError(isLimit ? `${msg}${!isLifetime ? t('learningPath.rmUpgradeHintDaily') : ''}` : msg);
    }
  };

  const openRoadmap = async (roadmapId) => {
    setViewRoadmap({ loading: true, data: null });
    setCurrentRoadmap(null);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    try {
      const { data } = await roadmapsApi.get(`/roadmaps/ai/${roadmapId}`);
      setViewRoadmap({ loading: false, data: data.roadmap });
    } catch { setViewRoadmap({ loading: false, data: null }); }
  };

  const quotaExhausted = usage && usage.remaining === 0 && !isLifetime;

  if (!isPaidUser) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-indigo-950/50 border border-indigo-800/40 mb-6 text-4xl">🗺️</div>
        <h2 className="text-2xl font-bold text-white mb-3">{t('learningPath.rmGenTitle')}</h2>
        <p className="text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
          {t('learningPath.rmGateDesc')}
        </p>
        <Link to="/membership" className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity">
          ✦ {t('learningPath.rmUpgradePro')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* Quota meter */}
      {usage && <RoadmapQuotaMeter usage={usage} isLifetime={isLifetime} />}

      {/* Generator card */}
      <div className="rounded-2xl p-6" style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-white">{t('learningPath.rmGenTitle')}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{t('learningPath.rmGenSubtitle')}</p>
        </div>

        {/* Quick topic chips */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t('learningPath.rmQuickPicks')}</p>
          <div className="flex flex-wrap gap-2">
            {ROADMAP_TOPICS.map(chip => (
              <button key={chip.labelKey}
                onClick={() => { if (!generating) { setTopic(chip.prompt); setDays(chip.days); } }}
                disabled={generating}
                className="text-xs px-3 py-1.5 rounded-full transition-all disabled:opacity-50 hover:scale-105"
                style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#a5b4fc' }}>
                {t(chip.labelKey)} · {chip.days}d
              </button>
            ))}
          </div>
        </div>

        {/* Topic textarea */}
        <div className="mb-4">
          <textarea
            value={topic}
            onChange={e => !generating && setTopic(e.target.value)}
            disabled={generating}
            placeholder={t('learningPath.rmTopicPlaceholder')}
            rows={6}
            className="input-field resize-none text-sm leading-relaxed disabled:opacity-60"
            style={{ fontFamily: 'inherit' }}
          />
          <p className="text-xs text-gray-600 mt-1 text-right">{t('learningPath.rmChars', { count: topic.length })}</p>
        </div>

        {/* Days selector */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('learningPath.rmDuration')}</label>
            <span className="text-sm font-bold text-indigo-300">{t('learningPath.rmDaysCount', { days })}</span>
          </div>
          <input type="range" min={7} max={90} step={1} value={days}
            onChange={e => !generating && setDays(Number(e.target.value))}
            disabled={generating}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer disabled:opacity-60"
            style={{ accentColor: '#6366f1', background: `linear-gradient(to right, #6366f1 ${((days-7)/83)*100}%, var(--lx-muted) 0%)` }}
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>{t('learningPath.rmRangeMin')}</span><span>30</span><span>60</span><span>{t('learningPath.rmRangeMax')}</span>
          </div>
        </div>

        {/* CTA */}
        {generating ? (
          <div className="rounded-xl px-5 py-4 flex items-center gap-4"
            style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
            <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-indigo-300">{t('learningPath.rmGenerating', { days })}</p>
              <p className="text-xs text-indigo-400/70 mt-0.5">{t(ROADMAP_PROGRESS_KEYS[progressStep])}</p>
            </div>
          </div>
        ) : quotaExhausted ? (
          <div className="rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            style={{ background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.25)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-300">{t('learningPath.rmQuotaUsedTitle')}</p>
              <p className="text-xs text-amber-400/70 mt-0.5">{t('learningPath.rmQuotaUsedDesc')}</p>
            </div>
            <Link to="/membership"
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)', color: '#1c1400' }}>
              ♾ {t('learningPath.rmUpgradeLifetime')}
            </Link>
          </div>
        ) : (
          <button onClick={generate} disabled={!topic.trim()}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}>
            🗺️ {t('learningPath.rmGenerateBtn')}
          </button>
        )}
        {genError && <p className="mt-3 text-xs text-red-400">{genError}</p>}
      </div>

      {/* Result */}
      <div ref={resultsRef}>
        {viewRoadmap && (
          <div className="rounded-2xl p-6" style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
            {viewRoadmap.loading ? (
              <div className="flex items-center gap-3 py-8 justify-center">
                <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-gray-500 text-sm">{t('learningPath.rmLoadingRoadmap')}</span>
              </div>
            ) : viewRoadmap.data ? (
              <RoadmapDetail item={viewRoadmap.data} onClose={() => setViewRoadmap(null)} />
            ) : null}
          </div>
        )}
        {currentRoadmap && !viewRoadmap && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">🗺️ {t('learningPath.rmYourRoadmap')}</p>
              <button onClick={() => setCurrentRoadmap(null)} className="text-xs text-gray-500 hover:text-gray-300">✕ {t('learningPath.rmDismiss')}</button>
            </div>
            <RoadmapDetail item={currentRoadmap} onClose={() => setCurrentRoadmap(null)} />
          </div>
        )}
      </div>

      {/* History */}
      <div>
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('learningPath.rmSavedRoadmaps')}</h3>
        {histLoading ? (
          <div className="flex items-center gap-2 py-6 text-gray-500 text-sm">
            <div className="w-4 h-4 border border-gray-500 border-t-transparent rounded-full animate-spin" /> {t('learningPath.rmLoading')}
          </div>
        ) : !roadmaps?.length ? (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
            <p className="text-3xl mb-2">🗺️</p>
            <p className="text-sm text-gray-500">{t('learningPath.rmNoRoadmaps')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {roadmaps.map(rm => (
              <button key={rm.roadmap_id} onClick={() => openRoadmap(rm.roadmap_id)}
                className="w-full text-left rounded-2xl border p-4 transition-all hover:border-indigo-500/40"
                style={{ background: 'var(--lx-card)', borderColor: 'var(--lx-border)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white truncate">{rm.roadmap?.title || rm.topic}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{rm.topic_preview}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-600">📅 {t('learningPath.rmDaysCount', { days: rm.total_days })}</span>
                      <span className="text-xs text-gray-700">
                        {rm.created_at ? new Date(rm.created_at).toLocaleDateString(i18n.language || 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </span>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-gray-600 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LearningPath() {
  const { t } = useTranslation();
  const { pathSlug } = useParams();
  const navigate     = useNavigate();
  const { user, isPaid, isLifetime } = useAuth();
  const isPaidUser   = isPaid || isLifetime;

  const [pageView, setPageView] = useState(pathSlug === 'roadmap' ? 'roadmap' : 'paths');

  // Sync view when navigating directly to /learning-paths/roadmap
  useEffect(() => {
    setPageView(pathSlug === 'roadmap' ? 'roadmap' : 'paths');
  }, [pathSlug]);

  const path = LEARNING_PATHS[pathSlug];

  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // redirect unknown slugs
  useEffect(() => {
    if (pathSlug === 'roadmap') return; // handled by pageView state, no redirect needed
    if (!path) navigate('/learning-paths/ai', { replace: true });
  }, [path, pathSlug, navigate]);

  useEffect(() => {
    if (!path) return;

    // Serve from cache if already fetched
    if (pathCache[path.slug]) {
      setPosts(pathCache[path.slug]);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setPosts([]);
    api.get('/posts', { params: { tag: path.tagSlug, limit: 100, order: 'asc' } })
      .then(({ data }) => {
        const fetched = data.posts || [];
        pathCache[path.slug] = fetched;
        setPosts(fetched);
      })
      .catch(() => setError(t('learningPath.loadError')))
      .finally(() => setLoading(false));
  }, [path, t]);

  // When showing the AI Roadmap view, path may be undefined — that's fine, render the roadmap tab directly
  if (!path && pathSlug !== 'roadmap') return null;

  const published = path ? posts.length : 0;
  const remaining = path ? Math.max(0, path.totalDays - published) : 0;

  // Build day slots: published posts first, then coming-soon placeholders
  const daySlots = posts.map((p, i) => {
    const day = extractDay(p.title) ?? (i + 1);
    return { type: 'post', post: p, dayNum: day };
  });

  // Show a few upcoming "coming soon" placeholder cards (max 5 or until totalDays)
  const previewCount = Math.min(remaining, 5);
  const lastPublishedDay = daySlots.length > 0
    ? (daySlots[daySlots.length - 1].dayNum)
    : 0;
  for (let i = 1; i <= previewCount; i++) {
    daySlots.push({ type: 'soon', dayNum: lastPublishedDay + i });
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* Top-level view toggle */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
            <button
              onClick={() => path ? setPageView('paths') : navigate('/learning-paths/ai')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${pageView === 'paths' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>
              📚 {t('learningPath.navPaths')}
            </button>
            {/* Signed out, send them to the gated route rather than the
                upgrade prompt: an account comes before a subscription. */}
            <button onClick={() => (user ? setPageView('roadmap') : navigate('/learning-paths/roadmap'))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${pageView === 'roadmap' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}>
              🗺️ {t('learningPath.navRoadmap')}
            </button>
          </div>
        </div>

        {/* AI Roadmap view */}
        {pageView === 'roadmap' && (
          <AiRoadmapTab isLifetime={isLifetime} isPaidUser={isPaidUser} />
        )}

        {/* Learning Paths view — only renders when a valid path exists */}
        {pageView === 'paths' && path && (
        <>
        {/* Path selector */}
        <PathTabs current={pathSlug} />

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">{path.icon}</span>
                <div>
                  <h1 className="text-3xl font-bold text-white">{t(path.labelKey)}</h1>
                  {path.progressNoteKey && (
                    <span
                      className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full mt-1"
                      style={{ background: path.accentBg, border: `1px solid ${path.accentBorder}`, color: path.accentText }}
                    >
                      🚀 {t(path.progressNoteKey)}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed max-w-2xl">{t(path.descKey)}</p>
            </div>

            {/* Progress ring summary */}
            {!loading && published > 0 && (
              <div
                className="flex-shrink-0 flex flex-col items-center justify-center rounded-2xl px-6 py-4 text-center"
                style={{ background: path.accentBg, border: `1px solid ${path.accentBorder}` }}
              >
                <div className="text-3xl font-bold" style={{ color: path.accentText }}>
                  {published}
                  <span className="text-lg text-gray-500">/{path.totalDays}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: path.accentText }}>{t('learningPath.daysPublished')}</p>
                {remaining > 0 && (
                  <p className="text-xs text-gray-600 mt-1">{t('learningPath.comingSoonShort', { count: remaining })}</p>
                )}
              </div>
            )}
          </div>

          {/* Progress bar */}
          {!loading && published > 0 && (
            <div className="mt-6">
              <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                <span>{t('learningPath.progress')}</span>
                <span>{t('learningPath.percentComplete', { pct: Math.round((published / path.totalDays) * 100) })}</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--lx-muted)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${(published / path.totalDays) * 100}%`,
                    background: `linear-gradient(90deg, ${path.accentFrom}, ${path.accentTo})`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sibling track — e.g. the 30-day and 100-day MLOps series point at each
            other, so landing on one surfaces the other instead of hiding it
            behind the nav. */}
        {path.relatedPath && LEARNING_PATHS[path.relatedPath] && (() => {
          const other = LEARNING_PATHS[path.relatedPath];
          return (
            <Link
              to={`/learning-paths/${other.slug}`}
              className="mb-8 rounded-2xl p-4 flex items-center gap-4 transition-colors group"
              style={{ background: other.accentBg, border: `1px solid ${other.accentBorder}`, display: 'flex' }}
            >
              <span className="text-2xl flex-shrink-0">{other.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">
                  {t('learningPath.alsoAvailable', { name: t(other.labelKey) })}
                </p>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{t(other.descKey)}</p>
              </div>
              <span
                className="text-sm font-medium flex-shrink-0 group-hover:translate-x-0.5 transition-transform"
                style={{ color: other.accentText }}
              >
                →
              </span>
            </Link>
          );
        })()}

        {/* Free-user nudge */}
        {!isPaidUser && published > 0 && (
          <div
            className="mb-8 rounded-2xl p-4 flex items-center gap-4"
            style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}
          >
            <span className="text-2xl flex-shrink-0">🔒</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#f59e0b">
                  <path d="M2 19l2-8 5 4 3-7 3 7 5-4 2 8H2zm18-11a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
                </svg>
                {t('learningPath.premiumTitle')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{t('learningPath.premiumDesc')}</p>
            </div>
            <Link
              to="/membership"
              className="flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90"
              style={{ background: `linear-gradient(135deg, ${path.accentFrom}, ${path.accentTo})` }}
            >
              {t('learningPath.upgrade')}
            </Link>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center gap-4 py-24">
            <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${path.accentFrom} transparent transparent transparent` }} />
            <p className="text-gray-500 text-sm">{t('learningPath.loadingPath', { path: t(path.labelKey) })}</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="text-center py-16">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                setError('');
                api.get('/posts', { params: { tag: path.tagSlug, limit: 100, order: 'asc' } })
                  .then(({ data }) => setPosts(data.posts || []))
                  .catch(() => setError(t('learningPath.loadError')))
                  .finally(() => setLoading(false));
              }}
              className="btn-secondary"
            >
              {t('learningPath.tryAgain')}
            </button>
          </div>
        )}

        {/* Day grid */}
        {!loading && !error && (
          <>
            {daySlots.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-5xl mb-4">{path.icon}</p>
                <p className="text-white font-semibold text-lg mb-2">{t('learningPath.comingSoonTitle', { path: t(path.labelKey) })}</p>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  {t('learningPath.comingSoonDesc')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {daySlots.map((slot, i) =>
                  slot.type === 'post' ? (
                    <DayCard
                      key={slot.post.id}
                      post={slot.post}
                      dayNum={slot.dayNum}
                      path={path}
                      index={i}
                    />
                  ) : (
                    <ComingSoonCard key={`soon-${slot.dayNum}`} dayNum={slot.dayNum} path={path} />
                  )
                )}

                {/* "More coming" card when there are many upcoming days */}
                {remaining > previewCount && (
                  <div
                    className="col-span-full rounded-2xl border p-6 text-center opacity-50"
                    style={{ borderColor: 'var(--lx-border)', borderStyle: 'dashed' }}
                  >
                    <p className="text-sm text-gray-500">
                      {t('learningPath.moreDays', { count: remaining - previewCount, path: t(path.labelKey) })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </> /* end paths view */
        )}

      </div>
    </Layout>
  );
}
